export function initSelectDropdowns() {
  const allDropdowns = document.querySelectorAll<HTMLElement>(
    '[data-select-wrapper]',
  );

  allDropdowns.forEach((dropdown) => {
    const isForm = dropdown.dataset.selectWrapper === 'True';
    if (isForm) {
      initSelectForm(dropdown);
    } else {
      initSelectMenu(dropdown);
    }
  });
}

// How the custom select dropdown works:
// 1. Hide the actual select and the OptionsHandler
// 2. Loop through all the select options and create a new div for each one taking their value
// 3. onClick():
// 3.1. Replace the placeholder with that selected element
// 3.2. Select the option at the selected fake option's index using select.selectedIndex = index
// 3.3. Hide the fake options

export function initSelectForm(dropdown: HTMLElement) {
  const selectButton = dropdown.querySelector(
    '[data-select-button]',
  ) as HTMLElement;
  const selectGroup = dropdown.querySelector(
    '[data-select-group]',
  ) as HTMLSelectElement;
  const placeHolder = dropdown.querySelector(
    '[data-select-placeholder]',
  ) as HTMLElement;
  const options =
    selectGroup.querySelectorAll<HTMLOptionElement>('[data-select-item]'); // Get all of the options in the real select

  // If no placeholder - set default value to first option
  const placeholderText = placeHolder.dataset.selectPlaceholder;
  if (
    (placeholderText === undefined || placeholderText.length === 0) &&
    options.length > 0
  ) {
    selectGroup.selectedIndex = 0;
    placeHolder.textContent = options[0].textContent;
    placeHolder.classList.remove('text-muted-foreground');
    placeHolder.classList.add('text-black');
  }
  // Creating the custom select dropdown
  const customSelectGroup = document.createElement('div');
  customSelectGroup.setAttribute(
    'class',
    'flex flex-col bg-white gap-2 p-1 rounded-sm absolute w-full mt-2 border shadow-md invisible',
  );
  customSelectGroup.setAttribute('data-select-custom-group', '');
  dropdown.appendChild(customSelectGroup);

  // Open select options
  selectButton.addEventListener('click', () => {
    customSelectGroup.classList.toggle('invisible');
    selectButton.dataset.state = 'open';
    selectButton.setAttribute('aria-expanded', 'true');
  });

  // Close the menu when clicking outside
  document.addEventListener('click', (event) => {
    const target = event.target as Node;

    if (!customSelectGroup.contains(target) && !selectButton.contains(target)) {
      customSelectGroup.classList.add('invisible');
    }
  });

  let count = 0;
  // Populate the custom select list with the options from the real select
  options.forEach((option, index) => {
    if (option.hasAttribute('disabled')) {
      count++;
      return;
    }

    const optionText = option.textContent;
    const customOption = document.createElement('div');
    customOption.textContent = optionText;

    customOption.setAttribute(
      'class',
      'bg-white px-4 py-2 w-full rounded-sm hover:bg-neutral-100 cursor-pointer text-base',
    );
    customOption.setAttribute('data-select-custom-option', option.value);

    // Accessibility stuff
    customOption.setAttribute('tabindex', '0');
    customOption.addEventListener('keydown', (event) => {
      const keyboardEvent = event as KeyboardEvent;
      const optionsArray = Array.from(customSelectGroup.children);
      const lastIndex = optionsArray.length - 1;

      keyboardEvent.preventDefault();
      // Navigating options
      if (keyboardEvent.key === 'ArrowUp') {
        if (index - count > 0) {
          (optionsArray[index - 1 - count] as HTMLElement).focus();
        } else {
          (optionsArray[lastIndex] as HTMLElement).focus();
        }
      }

      if (keyboardEvent.key === 'ArrowDown') {
        if (index - count === lastIndex) {
          (optionsArray[0] as HTMLElement).focus();
        } else {
          (optionsArray[index + 1 - count] as HTMLElement).focus();
        }
      }

      // Selecting options
      if (keyboardEvent.key === ' ' || keyboardEvent.key === 'Enter') {
        placeHolder.textContent = customOption.textContent; // Set placeholder to customOption
        placeHolder.classList.remove('text-muted-foreground');
        placeHolder.classList.add('text-black');
        selectGroup.selectedIndex = index; // Select the actual option

        selectButton.dataset.state = 'closed';
        selectButton.setAttribute('aria-expanded', 'false');

        selectButton.focus();
        customSelectGroup.classList.toggle('invisible');
      }
    });

    // Give it an index value
    const optionElement = option as HTMLElement;
    optionElement.dataset.selectItem = index.toString();

    // Select an option
    customOption.addEventListener('click', () => {
      placeHolder.textContent = customOption.textContent; // Set placeholder to customOption
      placeHolder.classList.remove('text-muted-foreground');
      placeHolder.classList.add('text-black');
      selectGroup.selectedIndex = index; // Select the actual option

      selectButton.dataset.state = 'closed';
      selectButton.setAttribute('aria-expanded', 'false');

      customSelectGroup.classList.toggle('invisible');
    });

    customSelectGroup.appendChild(customOption);
  });
}

export function initSelectMenu(dropdown: HTMLElement) {
  const selectButton = dropdown.querySelector(
    '[data-select-button]',
  ) as HTMLElement;
  const selectGroup = dropdown.querySelector(
    '[data-select-group]',
  ) as HTMLSelectElement;
  const placeHolder = dropdown.querySelector(
    '[data-select-placeholder]',
  ) as HTMLElement;
  const options = selectGroup.querySelectorAll('[data-select-item]');

  // Open select options
  selectButton.addEventListener('click', () => {
    selectGroup.classList.toggle('invisible');
    selectButton.dataset.state = 'open';
    selectButton.setAttribute('aria-expanded', 'true');
  });

  // Hide menu if mouse clicks elsewhere
  document.addEventListener('click', (event) => {
    const target = event.target as Node;

    if (!selectGroup.contains(target) && !selectButton.contains(target)) {
      selectGroup.classList.add('invisible');
    }
  });

  let index = 0;
  // Option event listeners
  options.forEach((option) => {
    // Replace placeholder when option is clicked
    option.addEventListener('click', () => {
      placeHolder.textContent = option.textContent; // Set placeholder to customOption
      placeHolder.classList.remove('text-muted-foreground');
      placeHolder.classList.add('text-black');

      selectButton.dataset.state = 'closed';
      selectButton.setAttribute('aria-expanded', 'false');

      selectGroup.classList.toggle('invisible');
    });

    // Make options selectable via keyboard
    option.setAttribute('tabindex', '0');
    option.addEventListener('keyup', (event) => {
      const keyboardEvent = event as KeyboardEvent;
      const lastIndex = options.length - 1;

      keyboardEvent.preventDefault(); // Stop scrolling;

      // Navigating options
      switch (keyboardEvent.key) {
        case 'ArrowUp':
          if (index > 0) {
            index--;
          } else {
            index = lastIndex;
          }
          (options[index] as HTMLElement).focus();
          break;
        case 'ArrowDown':
          if (index === lastIndex) {
            index = 0;
          } else {
            index++;
          }
          (options[index] as HTMLElement).focus();
          break;
        case ' ':
        case 'Enter':
          placeHolder.textContent = option.textContent; // Set placeholder to customOption
          placeHolder.classList.remove('text-muted-foreground');
          placeHolder.classList.add('text-black');

          selectButton.dataset.state = 'closed';
          selectButton.setAttribute('aria-expanded', 'false');

          selectGroup.classList.toggle('invisible');
          break;
        default:
          break;
      }
    });
    option.addEventListener('keydown', (event) => {
      event.preventDefault();
    });
  });
}
